/**
 * Executable checks for the deliberately small procedural-asset contract.
 *
 * This is a bounded reproducibility check, not a proof of factory purity: two
 * fresh calls can agree while still reading hidden global state. It catches the
 * failures that otherwise make the workbench and review screenshots misleading.
 */

import * as THREE from 'three';
import { isSurfaceMaterial } from './materials';
import type { AssetDefinition } from './types';

const KEBAB_CASE = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

function assetId(asset: AssetDefinition): string {
  const value = (asset as unknown as { id?: unknown }).id;
  return typeof value === 'string' ? value : String(value);
}

function fail(asset: AssetDefinition, part: string, problem: string): never {
  throw new Error(`asset "${assetId(asset)}" ${part}: ${problem}`);
}

function nodeLabel(node: THREE.Object3D, index: number): string {
  return `node "${node.name || `${node.type}#${index}`}"`;
}

function finite(asset: AssetDefinition, part: string, value: number): void {
  if (!Number.isFinite(value)) fail(asset, part, 'contains a non-finite value');
}

function hashText(hash: number, value: string): number {
  let next = hash;
  for (let index = 0; index < value.length; index += 1) {
    next ^= value.charCodeAt(index);
    next = Math.imul(next, 0x01000193);
  }
  return next >>> 0;
}

function hashNumber(hash: number, value: number): number {
  return hashText(hash, String(value));
}

function attributeFingerprint(
  asset: AssetDefinition,
  attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  part: string,
  name: string,
  hash: number,
): number {
  let next = hashText(hash, name);
  for (let index = 0; index < attribute.array.length; index += 1) {
    const value = attribute.array[index];
    if (value === undefined) fail(asset, part, `geometry attribute "${name}" is missing value ${index}`);
    finite(asset, `${part} geometry attribute "${name}" value ${index}`, value);
    next = hashNumber(next, value);
  }
  return next;
}

function geometryFingerprint(asset: AssetDefinition, geometry: THREE.BufferGeometry, part: string, hash: number): number {
  let next = hashText(hash, geometry.type);
  const attributes = Object.entries(geometry.attributes).sort(([left], [right]) => left.localeCompare(right));
  for (const [name, attribute] of attributes) {
    next = attributeFingerprint(asset, attribute, part, name, next);
  }
  if (geometry.index) next = attributeFingerprint(asset, geometry.index, part, 'index', next);
  next = hashText(next, `draw:${geometry.drawRange.start},${geometry.drawRange.count}`);
  for (const group of geometry.groups) {
    next = hashText(next, `group:${group.start},${group.count},${group.materialIndex}`);
  }
  return next;
}

function materialFingerprint(asset: AssetDefinition, material: THREE.Material, part: string, hash: number): number {
  if (!isSurfaceMaterial(material)) {
    fail(asset, part, 'uses material not returned by surface()');
  }
  const surfaceKey: unknown = (material.userData as Record<string, unknown>)['surfaceKey'];
  if (typeof surfaceKey !== 'string' || surfaceKey.length === 0) {
    fail(asset, part, 'surface() material has no surfaceKey evidence');
  }
  return hashText(hashText(hash, material.type), surfaceKey);
}

function isBufferGeometry(value: unknown): value is THREE.BufferGeometry {
  return value instanceof THREE.BufferGeometry;
}

function isMaterial(value: unknown): value is THREE.Material {
  return value instanceof THREE.Material;
}

/** Validates author-supplied registry metadata without constructing an asset. */
export function validateAssetMetadata(asset: AssetDefinition): void {
  const metadata = asset as unknown as {
    id?: unknown;
    label?: unknown;
    description?: unknown;
    footprint?: { width?: unknown; height?: unknown; length?: unknown } | null;
  };
  if (typeof metadata.id !== 'string' || !KEBAB_CASE.test(metadata.id)) {
    fail(asset, 'metadata id', 'must be nonempty kebab-case');
  }
  if (typeof metadata.label !== 'string' || metadata.label.trim().length === 0) {
    fail(asset, 'metadata label', 'must be nonempty');
  }
  if (typeof metadata.description !== 'string' || metadata.description.trim().length === 0) {
    fail(asset, 'metadata description', 'must be nonempty');
  }
  if (metadata.footprint === null || typeof metadata.footprint !== 'object') {
    fail(asset, 'metadata footprint', 'must define width, height, and length');
  }
  for (const dimension of ['width', 'height', 'length'] as const) {
    const value = metadata.footprint[dimension];
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      fail(asset, `metadata footprint.${dimension}`, 'must be finite and positive');
    }
  }
}

function fingerprint(asset: AssetDefinition, root: THREE.Group): string {
  let hash = 0x811c9dc5;
  let nodes = 0;
  root.traverse((node) => {
    const part = nodeLabel(node, nodes);
    nodes += 1;
    if (node instanceof THREE.Light || node instanceof THREE.Camera) {
      fail(asset, part, 'must not contain a Light or Camera');
    }
    const values = [
      node.position.x, node.position.y, node.position.z,
      node.quaternion.x, node.quaternion.y, node.quaternion.z, node.quaternion.w,
      node.scale.x, node.scale.y, node.scale.z,
    ];
    for (const value of values) finite(asset, part, value);
    hash = hashText(hash, `${node.type}|${node.name}|${values.join(',')}`);
    // Lines, points and sprites are assets too. Do not make Mesh the accidental
    // boundary of this check: any reachable node that bears geometry or material
    // must be held to the same finite-data and surface-provenance contract.
    const renderable = node as unknown as { geometry?: unknown; material?: unknown };
    if (renderable.geometry !== undefined) {
      if (!isBufferGeometry(renderable.geometry)) {
        fail(asset, part, 'has geometry that is not a BufferGeometry');
      }
      hash = geometryFingerprint(asset, renderable.geometry, part, hash);
    }
    if (renderable.material !== undefined) {
      const materials = Array.isArray(renderable.material) ? renderable.material : [renderable.material];
      for (const material of materials) {
        if (!isMaterial(material)) fail(asset, part, 'has a non-Material material value');
        hash = materialFingerprint(asset, material, part, hash);
      }
    }
    if (node instanceof THREE.InstancedMesh) {
      hash = hashNumber(hash, node.count);
      hash = attributeFingerprint(asset, node.instanceMatrix, part, 'instanceMatrix', hash);
      if (node.instanceColor) {
        hash = attributeFingerprint(asset, node.instanceColor, part, 'instanceColor', hash);
      }
    }
  });
  return `${nodes}:${hash.toString(16)}`;
}

/** Builds twice and requires matching structure and geometry, excluding UUIDs. */
export function validateAssetFactory(asset: AssetDefinition): void {
  validateAssetMetadata(asset);
  const first = asset.create();
  const second = asset.create();
  const firstFingerprint = fingerprint(asset, first);
  const secondFingerprint = fingerprint(asset, second);
  if (firstFingerprint !== secondFingerprint) {
    fail(asset, 'factory output', `is not reproducible (${firstFingerprint} !== ${secondFingerprint})`);
  }
}
